import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  GraduationCap, 
  Users, 
  Brain, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Database,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Shield,
  BookOpen,
  Search,
  FileText,
  Globe,
  Calendar,
  HelpCircle,
  Info
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Plain-language explanations shown via the ℹ️ info icons across the AI Training page.
const TOOLTIPS = {
  // Dashboard
  readiness_score: "Legacy readiness score (0–100) based on KB linked, content present, coaching notes, feedback and guard rails. Being superseded by the live Quality % and Progress %.",
  quality: "How good the AI is now: average of the approval rate (👍 vs 👎) and the grounded rate (answers that used your Knowledge Base / Zoho vs general knowledge).",
  progress: "How much training work is done: a weighted blend of examples reviewed, KB chunk coverage and signals resolved — measured against your Setup targets.",
  knowledge_bases: "How many knowledge bases are linked to this AI actor.",
  coaching_notes: "Behavioral guidelines learned from past reviews that are injected into the AI's prompt.",
  user_feedback: "Counts of 👍 / 👎 left by end-users in chat.",
  configuration: "Guard-rail status, strictness and conversation totals for this actor.",
  // Setup
  training_mode: "Turns this actor's public link into a trainer console: visitors are asked for name + email, and verified trainers get 👍/👎 rating controls under each reply. Off = normal public chat.",
  trainers: "People allowed to train this actor, by name + email only (no password). Only these emails get rating controls on the public link.",
  targets: "Your goals — examples to review, target KB chunk-coverage %, and signals to resolve. These drive the Training Progress % calculation.",
  corrected_boost: "How strongly approved corrections outrank regular chunks during search, so the corrected answer is the one the AI uses.",
  live_signal: "When ON, after each normal-user turn an LLM checks if the user seemed dissatisfied with the previous reply and, if so, drops a 🟣 Live signal into the Review Queue. Works even when Training Mode is OFF. Costs one extra LLM call per turn — off by default.",
  live_sensitivity: "How easily live signals are raised: Low = only explicit complaints, Medium = clear dissatisfaction, High = any hint of confusion.",
  relevance_coaching: "Normally ALL coaching notes are added to every prompt. When ON, only the coaching notes relevant to the current question are injected — keeps the prompt focused when you have many notes.",
  // Review Queue
  queue: "Your inbox of pending feedback: 🟢 Trainer ratings and 🟣 Live signals, each tagged Fact or Behavioral. Approve facts → Knowledge Base, behaviors → Coaching, or Dismiss false alarms.",
  // Insights
  insights_coverage: "KB coverage measured by the share of CHUNKS exercised in training (not whole documents). Any document linked to this actor appears here automatically — starting at 0% until a question touches it.",
  live_pending: "Number of auto-detected live signals waiting for your review.",
  quality_trend: "Quality % captured daily when you open Insights — a line appears after a few days of snapshots.",
};

function InfoTip({ text, id }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex align-middle ml-1 text-slate-400 hover:text-slate-600" data-testid={id ? `tip-${id}` : undefined} aria-label="More info">
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AITraining() {
  const { toast } = useToast();
  
  // State
  const [actors, setActors] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  
  // Guide modal state
  const [showGuide, setShowGuide] = useState(false);

  // Training Setup (self-learning Phase 1)
  const [trainingMode, setTrainingMode] = useState(false);
  const [trainerList, setTrainerList] = useState([]);
  const [newTrainerName, setNewTrainerName] = useState("");
  const [newTrainerEmail, setNewTrainerEmail] = useState("");
  const [trainTargets, setTrainTargets] = useState({ examples_reviewed: 100, kb_chunk_coverage_pct: 80, signals_resolved: 40, weight_examples: 0.40, weight_coverage: 0.30, weight_signals: 0.30 });
  const [correctedBoost, setCorrectedBoost] = useState(0.25);
  const [liveSignal, setLiveSignal] = useState(false);
  const [liveSensitivity, setLiveSensitivity] = useState("medium");
  const [relevanceCoaching, setRelevanceCoaching] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [qualityHistory, setQualityHistory] = useState([]);

  // Review Queue + Insights (Phase 2)
  const [queue, setQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [actingId, setActingId] = useState(null);

  useEffect(() => {
    fetchActors();
  }, []);

  useEffect(() => {
    if (selectedActor) {
      fetchDashboard();
      fetchTrainingSettings();
      fetchQueue();
      fetchMetrics();
    }
  }, [selectedActor]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const fetchTrainingSettings = async () => {
    if (!selectedActor) return;
    try {
      const res = await axios.get(`${API}/api/admin/ai-actors/${selectedActor}/training-settings`, authHeaders());
      setTrainingMode(!!res.data.training_mode);
      setTrainerList(res.data.trainer_list || []);
      if (res.data.training_targets) setTrainTargets({ ...trainTargets, ...res.data.training_targets });
      setCorrectedBoost(res.data.corrected_chunk_boost ?? 0.25);
      setLiveSignal(!!res.data.live_signal_detection);
      setLiveSensitivity(res.data.live_signal_sensitivity || "medium");
      setRelevanceCoaching(!!res.data.relevance_scoped_coaching);
    } catch (e) { /* non-blocking */ }
  };

  const addTrainer = () => {
    if (!newTrainerName.trim() || !newTrainerEmail.trim()) return;
    setTrainerList((p) => [...p, { name: newTrainerName.trim(), email: newTrainerEmail.trim() }]);
    setNewTrainerName(""); setNewTrainerEmail("");
  };

  const removeTrainer = (email) => setTrainerList((p) => p.filter((t) => t.email !== email));

  const saveTrainingSetup = async () => {
    if (!selectedActor) return;
    setSavingSetup(true);
    try {
      await axios.put(`${API}/api/admin/ai-actors/${selectedActor}/training-settings`, {
        training_mode: trainingMode,
        trainer_list: trainerList,
        training_targets: trainTargets,
        corrected_chunk_boost: correctedBoost,
        live_signal_detection: liveSignal,
        live_signal_sensitivity: liveSensitivity,
        relevance_scoped_coaching: relevanceCoaching,
      }, authHeaders());
      toast({ title: "Saved", description: "Training settings updated" });
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Failed to save", variant: "destructive" });
    } finally { setSavingSetup(false); }
  };

  const fetchQueue = async () => {
    if (!selectedActor) return;
    setLoadingQueue(true);
    try {
      const res = await axios.get(`${API}/api/admin/ai-actors/${selectedActor}/training/examples?status=pending`, authHeaders());
      setQueue(res.data.examples || []);
    } catch (e) { /* non-blocking */ } finally { setLoadingQueue(false); }
  };

  const fetchMetrics = async () => {
    if (!selectedActor) return;
    setLoadingMetrics(true);
    try {
      const res = await axios.get(`${API}/api/admin/ai-actors/${selectedActor}/training/metrics`, authHeaders());
      setMetrics(res.data);
    } catch (e) { /* non-blocking */ } finally { setLoadingMetrics(false); }
    try {
      const h = await axios.get(`${API}/api/admin/ai-actors/${selectedActor}/training/metrics/history`, authHeaders());
      setQualityHistory(h.data.history || []);
    } catch (e) { /* non-blocking */ }
  };

  const applyExample = async (exId, routeTo) => {
    setActingId(exId);
    try {
      await axios.post(`${API}/api/admin/ai-actors/${selectedActor}/training/apply/${exId}`, { route_to: routeTo }, authHeaders());
      toast({ title: "Applied", description: routeTo === "kb" ? "Saved as a learned KB correction (boosted)" : "Saved as a coaching note" });
      fetchQueue(); fetchMetrics();
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Failed to apply", variant: "destructive" });
    } finally { setActingId(null); }
  };

  const dismissExample = async (exId) => {
    setActingId(exId);
    try {
      await axios.post(`${API}/api/admin/ai-actors/${selectedActor}/training/dismiss/${exId}`, {}, authHeaders());
      toast({ title: "Dismissed" });
      fetchQueue(); fetchMetrics();
    } catch (e) {
      toast({ title: "Error", description: "Failed to dismiss", variant: "destructive" });
    } finally { setActingId(null); }
  };

  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActors(response.data);
      if (response.data.length > 0) {
        setSelectedActor(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching actors:", error);
      toast({ title: "Error", description: "Failed to fetch AI actors", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    if (!selectedActor) return;
    setLoadingDashboard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/admin/ai-training/actor/${selectedActor}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("AI Training Dashboard Response:", response.data);
      console.log("Knowledge Bases:", response.data.knowledge_bases);
      console.log("Total Linked:", response.data.knowledge_bases?.total_linked);
      setDashboard(response.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast({ title: "Error", description: "Failed to fetch dashboard", variant: "destructive" });
    } finally {
      setLoadingDashboard(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (actors.length === 0) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-20 pb-20 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">No AI Actors Found</h2>
            <p className="text-gray-600">Create an AI Actor first to access training features.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">AI Training & Validation</h1>
          <p className="text-slate-600">Verify and validate your AI Actor's training with complete transparency</p>
        </div>
        <Button onClick={() => setShowGuide(true)} variant="outline" className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4" />
          View Guide
        </Button>
      </div>

      {/* Actor Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Users className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <Label>Select AI Actor</Label>
              <Select value={selectedActor} onValueChange={setSelectedActor}>
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Choose an AI Actor" />
                </SelectTrigger>
                <SelectContent>
                  {actors.map((actor) => (
                    <SelectItem key={actor.id} value={actor.id}>
                      {actor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard">
            <Target className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="setup" data-testid="setup-tab-trigger">
            <Users className="w-4 h-4 mr-2" />
            Setup
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="queue-tab-trigger">
            <MessageSquare className="w-4 h-4 mr-2" />
            Review Queue
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="insights-tab-trigger">
            <TrendingUp className="w-4 h-4 mr-2" />
            Insights
          </TabsTrigger>
        </TabsList>

        {/* Setup Tab (Self-Learning) */}
        <TabsContent value="setup" className="space-y-6" data-testid="setup-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-600" /> AI Training Configuration</CardTitle>
              <CardDescription>Turn on training mode for this actor and choose who can train it via the public profile link.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Training mode toggle */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">Training Mode<InfoTip id="training-mode" text={TOOLTIPS.training_mode} /></p>
                  <p className="text-xs text-muted-foreground">When ON, the public link asks visitors to identify, and verified trainers can rate replies.</p>
                </div>
                <button
                  onClick={() => setTrainingMode((v) => !v)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${trainingMode ? "bg-indigo-600" : "bg-slate-300"}`}
                  data-testid="training-mode-toggle">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${trainingMode ? "translate-x-6" : ""}`} />
                </button>
              </div>

              {/* Trainer list */}
              <div>
                <Label className="text-sm">Trainers (name + email — no password)<InfoTip id="trainers" text={TOOLTIPS.trainers} /></Label>
                <div className="mt-2 space-y-2">
                  {trainerList.length === 0 && <p className="text-xs text-muted-foreground">No trainers added yet.</p>}
                  {trainerList.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-sm" data-testid={`trainer-row-${i}`}>
                      <span>{t.name} · <span className="text-muted-foreground">{t.email}</span></span>
                      <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700" onClick={() => removeTrainer(t.email)} data-testid={`remove-trainer-${i}`}>Remove</Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input value={newTrainerName} onChange={(e) => setNewTrainerName(e.target.value)} placeholder="Name" className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="new-trainer-name" />
                  <input value={newTrainerEmail} onChange={(e) => setNewTrainerEmail(e.target.value)} placeholder="email@company.com" className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="new-trainer-email" />
                  <Button size="sm" variant="outline" onClick={addTrainer} data-testid="add-trainer-btn">+ Add</Button>
                </div>
              </div>

              {/* Targets */}
              <div>
                <Label className="text-sm">Targets<InfoTip id="targets" text={TOOLTIPS.targets} /></Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Examples to review</p>
                    <input type="number" value={trainTargets.examples_reviewed} onChange={(e) => setTrainTargets({ ...trainTargets, examples_reviewed: parseInt(e.target.value) || 0 })} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="target-examples" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">KB chunk coverage %</p>
                    <input type="number" value={trainTargets.kb_chunk_coverage_pct} onChange={(e) => setTrainTargets({ ...trainTargets, kb_chunk_coverage_pct: parseInt(e.target.value) || 0 })} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="target-coverage" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Signals to resolve</p>
                    <input type="number" value={trainTargets.signals_resolved} onChange={(e) => setTrainTargets({ ...trainTargets, signals_resolved: parseInt(e.target.value) || 0 })} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="target-signals" />
                  </div>
                </div>
              </div>

              {/* Corrected chunk boost */}
              <div>
                <Label className="text-sm">Corrected-chunk retrieval boost (%)<InfoTip id="corrected-boost" text={TOOLTIPS.corrected_boost} /></Label>
                <p className="text-xs text-muted-foreground mb-1">How strongly corrected/learned chunks outrank regular chunks during search.</p>
                <input type="number" value={Math.round(correctedBoost * 100)} onChange={(e) => setCorrectedBoost((parseInt(e.target.value) || 0) / 100)} className="w-32 text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="corrected-boost" />
              </div>

              {/* Phase 3: Live signal detection */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="pr-4">
                  <p className="font-medium text-sm">Live-signal detection<InfoTip id="live-signal" text={TOOLTIPS.live_signal} /></p>
                  <p className="text-xs text-muted-foreground">Auto-flag dissatisfied real users into the Review Queue (uses 1 extra LLM call per turn). Off by default.</p>
                </div>
                <button onClick={() => setLiveSignal((v) => !v)} className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${liveSignal ? "bg-indigo-600" : "bg-slate-300"}`} data-testid="live-signal-toggle">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${liveSignal ? "translate-x-6" : ""}`} />
                </button>
              </div>
              {liveSignal && (
                <div>
                  <Label className="text-sm">Detection sensitivity<InfoTip id="live-sensitivity" text={TOOLTIPS.live_sensitivity} /></Label>
                  <select value={liveSensitivity} onChange={(e) => setLiveSensitivity(e.target.value)} className="w-48 text-sm border border-slate-200 rounded px-2 py-1.5 mt-1 bg-white block" data-testid="live-sensitivity">
                    <option value="low">Low — only explicit complaints</option>
                    <option value="medium">Medium — clear dissatisfaction</option>
                    <option value="high">High — any hint of confusion</option>
                  </select>
                </div>
              )}

              {/* Phase 3: Relevance-scoped coaching */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="pr-4">
                  <p className="font-medium text-sm">Relevance-scoped coaching notes<InfoTip id="relevance-coaching" text={TOOLTIPS.relevance_coaching} /></p>
                  <p className="text-xs text-muted-foreground">Inject only the coaching notes relevant to each question instead of all of them.</p>
                </div>
                <button onClick={() => setRelevanceCoaching((v) => !v)} className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${relevanceCoaching ? "bg-indigo-600" : "bg-slate-300"}`} data-testid="relevance-coaching-toggle">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${relevanceCoaching ? "translate-x-6" : ""}`} />
                </button>
              </div>

              <Button onClick={saveTrainingSetup} disabled={savingSetup} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-training-setup-btn">
                {savingSetup ? "Saving…" : "Save"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Review Queue Tab */}
        <TabsContent value="queue" className="space-y-4" data-testid="queue-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-indigo-600" /> Review Queue<InfoTip id="review-queue" text={TOOLTIPS.queue} /></CardTitle>
              <CardDescription>Trainer ratings awaiting your review. Approve facts to the Knowledge Base or behaviors to coaching notes.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingQueue && <p className="text-sm text-muted-foreground">Loading…</p>}
              {!loadingQueue && queue.length === 0 && <p className="text-sm text-muted-foreground" data-testid="queue-empty">No pending items. Trainer ratings will appear here.</p>}
              {queue.map((ex) => (
                <div key={ex.id} className="border rounded-lg p-3 space-y-2" data-testid={`queue-item-${ex.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {ex.mode === "live"
                      ? <Badge variant="outline" className="bg-purple-50 text-purple-700" data-testid={`queue-live-${ex.id}`}>🟣 Live signal</Badge>
                      : <Badge variant="outline" className="bg-blue-50 text-blue-700">🟢 Trainer rating</Badge>}
                    <Badge variant="outline" className={ex.verdict === "good" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>{ex.verdict === "good" ? "👍 Good" : "👎 Bad"}</Badge>
                    {ex.error_type && <Badge variant="outline" className="bg-amber-50 text-amber-700 capitalize" data-testid={`queue-errortype-${ex.id}`}>{ex.error_type} error</Badge>}
                    <span className="text-xs text-muted-foreground">{ex.trainer_name}{ex.trainer_email ? ` · ${ex.trainer_email}` : ""}</span>
                  </div>
                  <p className="text-sm"><span className="text-muted-foreground">Q:</span> {ex.question}</p>
                  <p className="text-sm"><span className="text-muted-foreground">AI:</span> {ex.ai_answer}</p>
                  {ex.why_text && <p className="text-sm text-slate-600"><span className="text-muted-foreground">Why:</span> {ex.why_text}</p>}
                  {ex.corrected_answer && <p className="text-sm text-emerald-700"><span className="text-muted-foreground">Correct:</span> {ex.corrected_answer}</p>}
                  <div className="text-[11px] text-muted-foreground">Audit: {ex.response_source || "—"}{ex.chunk_ids_used?.length ? ` · chunks: ${ex.chunk_ids_used.length}` : ""}{ex.doc_ids_used?.length ? ` · docs: ${ex.doc_ids_used.length}` : ""}</div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={actingId === ex.id} onClick={() => applyExample(ex.id, "kb")} data-testid={`approve-kb-${ex.id}`}>Approve → KB</Button>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={actingId === ex.id} onClick={() => applyExample(ex.id, "coaching")} data-testid={`approve-coaching-${ex.id}`}>Approve → Coaching</Button>
                    <Button size="sm" variant="outline" disabled={actingId === ex.id} onClick={() => dismissExample(ex.id)} data-testid={`dismiss-${ex.id}`}>Dismiss</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-4" data-testid="insights-tab-content">
          {loadingMetrics && <p className="text-sm text-muted-foreground">Loading metrics…</p>}
          {metrics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-muted-foreground">Training Progress<InfoTip id="insights-progress" text={TOOLTIPS.progress} /></p><p className="text-3xl font-bold text-indigo-600" data-testid="metric-progress">{metrics.training_progress_pct}%</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-muted-foreground">Quality<InfoTip id="insights-quality" text={TOOLTIPS.quality} /></p><p className={`text-3xl font-bold ${metrics.quality_pct >= 80 ? "text-green-600" : metrics.quality_pct >= 60 ? "text-amber-600" : "text-red-600"}`} data-testid="metric-quality">{metrics.quality_pct}%</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-muted-foreground">KB Chunk Coverage<InfoTip id="insights-coverage" text={TOOLTIPS.insights_coverage} /></p><p className="text-3xl font-bold text-slate-800" data-testid="metric-coverage">{metrics.metrics.kb_chunk_coverage_pct}%</p><p className="text-[11px] text-muted-foreground">{metrics.metrics.covered_chunks}/{metrics.metrics.total_chunks} chunks</p></CardContent></Card>
                <Card><CardContent className="p-4 text-center"><p className="text-xs text-muted-foreground">Examples Reviewed</p><p className="text-3xl font-bold text-slate-800">{metrics.metrics.examples_reviewed}<span className="text-sm text-muted-foreground">/{metrics.metrics.target_examples}</span></p></CardContent></Card>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Approval rate</p><p className="text-xl font-semibold">{metrics.metrics.approval_rate}%</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Grounded rate</p><p className="text-xl font-semibold">{metrics.metrics.grounded_rate}%</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Signals resolved</p><p className="text-xl font-semibold">{metrics.metrics.signals_resolved}/{metrics.metrics.target_signals}</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Live signals pending<InfoTip id="insights-live-pending" text={TOOLTIPS.live_pending} /></p><p className="text-xl font-semibold text-purple-600" data-testid="metric-live-pending">{metrics.metrics.live_signals_pending ?? 0}</p></CardContent></Card>
              </div>
              {metrics.top_recurring_issues?.length > 0 && (
                <Card><CardContent className="p-3"><p className="text-sm font-medium mb-1">Top recurring issues</p>
                  <div className="flex gap-2 flex-wrap">{metrics.top_recurring_issues.map((t, i) => <Badge key={i} variant="outline" className="capitalize">{t.topic}: {t.count}</Badge>)}</div>
                </CardContent></Card>
              )}

              {/* Quality trend sparkline (Phase 3) */}
              <Card data-testid="quality-trend-card">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center">Quality % over time<InfoTip id="insights-trend" text={TOOLTIPS.quality_trend} /></CardTitle><CardDescription>Daily snapshots captured when you view Insights.</CardDescription></CardHeader>
                <CardContent>
                  {qualityHistory.length < 2 ? (
                    <p className="text-sm text-muted-foreground">Trend will appear after a few days of snapshots. ({qualityHistory.length} so far)</p>
                  ) : (
                    <svg viewBox="0 0 300 70" className="w-full h-20" preserveAspectRatio="none">
                      {(() => {
                        const pts = qualityHistory.map((s, i) => {
                          const x = (i / (qualityHistory.length - 1)) * 300;
                          const y = 70 - (Math.max(0, Math.min(100, s.quality_pct)) / 100) * 70;
                          return `${x.toFixed(1)},${y.toFixed(1)}`;
                        });
                        return <polyline fill="none" stroke="#4f46e5" strokeWidth="2" points={pts.join(" ")} />;
                      })()}
                    </svg>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center">Document Chunk Coverage<InfoTip id="insights-doc-coverage" text={TOOLTIPS.insights_coverage} /></CardTitle><CardDescription>Coverage measured by chunks exercised in training (not whole documents).</CardDescription></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="coverage-table">
                      <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2">Document</th><th className="py-2">Chunks covered</th><th className="py-2">Coverage</th><th className="py-2">Questions</th></tr></thead>
                      <tbody>
                        {metrics.document_coverage_list.slice(0, 50).map((d) => (
                          <tr key={d.doc_id} className="border-b last:border-0">
                            <td className="py-1.5 pr-2 truncate max-w-[280px]">{d.doc_name}</td>
                            <td className="py-1.5">{d.covered_chunks} / {d.total_chunks}</td>
                            <td className="py-1.5"><span className={d.coverage_pct >= 70 ? "text-green-600" : d.coverage_pct > 0 ? "text-amber-600" : "text-red-500"}>{d.coverage_pct}%{d.coverage_pct === 0 ? " ⚠" : ""}</span></td>
                            <td className="py-1.5">{d.tested_questions}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {loadingDashboard ? (
            <Card>
              <CardContent className="pt-20 pb-20 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </CardContent>
            </Card>
          ) : dashboard ? (
            <>
              {/* Training Score */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    Training Readiness Score<InfoTip id="readiness-score" text={TOOLTIPS.readiness_score} />
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Last updated: {dashboard.last_updated ? new Date(dashboard.last_updated).toLocaleString() : 'Never'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {metrics && (
                    <div className="flex gap-4 mb-4" data-testid="dashboard-new-scores">
                      <div className="flex-1 text-center p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                        <p className="text-xs text-muted-foreground">Quality (live self-learning)<InfoTip id="dash-quality" text={TOOLTIPS.quality} /></p>
                        <p className={`text-3xl font-bold ${metrics.quality_pct >= 80 ? "text-green-600" : metrics.quality_pct >= 60 ? "text-amber-600" : "text-red-600"}`}>{metrics.quality_pct}%</p>
                      </div>
                      <div className="flex-1 text-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <p className="text-xs text-muted-foreground">Training Progress<InfoTip id="dash-progress" text={TOOLTIPS.progress} /></p>
                        <p className="text-3xl font-bold text-indigo-600">{metrics.training_progress_pct}%</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="relative pt-1">
                        <div className="flex mb-2 items-center justify-between">
                          <div>
                            <span className="text-5xl font-bold text-blue-600">{dashboard.training_score}</span>
                            <span className="text-2xl text-gray-500">/100</span>
                          </div>
                        </div>
                        <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-blue-200">
                          <div
                            style={{ width: `${dashboard.training_score}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600 transition-all duration-500"
                          ></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{dashboard.knowledge_bases.total_items}</div>
                          <div className="text-xs text-gray-600">Knowledge Items</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{dashboard.coaching_notes.total}</div>
                          <div className="text-xs text-gray-600">Coaching Notes</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-amber-600">{dashboard.feedback.total}</div>
                          <div className="text-xs text-gray-600">Feedback Entries</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid - Split into manageable cards */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Knowledge Bases Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Database className="w-5 h-5 text-green-600" />
                      Knowledge Bases<InfoTip id="dash-kb" text={TOOLTIPS.knowledge_bases} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Linked KBs:</span>
                        <Badge variant="outline">{dashboard.knowledge_bases.total_linked}</Badge>
                      </div>
                      {dashboard.knowledge_bases.details.map((kb) => (
                        <div key={kb.id} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                          <span className="text-sm font-medium">{kb.name}</span>
                          <Badge>{kb.item_count} items</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Coaching Notes Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      Coaching Notes<InfoTip id="dash-coaching" text={TOOLTIPS.coaching_notes} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(dashboard.coaching_notes.by_category).map(([category, count]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{category}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                      {Object.keys(dashboard.coaching_notes.by_category).length === 0 && (
                        <p className="text-sm text-gray-500">No coaching notes yet</p>
                      )}
                      {dashboard.coaching_notes.from_feedback > 0 && (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MessageSquare className="w-3 h-3" />
                              <span>From Feedback</span>
                            </div>
                            <Badge variant="outline" className="bg-purple-50">{dashboard.coaching_notes.from_feedback}</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Feedback Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-amber-600" />
                      User Feedback<InfoTip id="dash-feedback" text={TOOLTIPS.user_feedback} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-green-600" />
                          <span className="text-sm">Positive</span>
                        </div>
                        <Badge className="bg-green-100 text-green-700">{dashboard.feedback.positive}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <ThumbsDown className="w-4 h-4 text-red-600" />
                          <span className="text-sm">Negative</span>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{dashboard.feedback.negative}</Badge>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Total</span>
                          <Badge>{dashboard.feedback.total}</Badge>
                        </div>
                      </div>
                      {dashboard.feedback.converted_to_coaching > 0 && (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4 text-purple-600" />
                              <span className="text-sm">Converted to Coaching</span>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">{dashboard.feedback.converted_to_coaching}</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Configuration Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      Configuration<InfoTip id="dash-config" text={TOOLTIPS.configuration} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Guard Rails</span>
                        <Badge variant={dashboard.guard_rails.enabled ? "default" : "secondary"}>
                          {dashboard.guard_rails.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      {dashboard.guard_rails.enabled && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Strictness</span>
                          <Badge variant="outline">{dashboard.guard_rails.strictness}</Badge>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-sm">Total Conversations</span>
                        <Badge>{dashboard.conversations.total}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </TabsContent>

      </Tabs>

      {/* Guide Modal */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              AI Training Page - Complete Feature Guide
            </DialogTitle>
            <DialogDescription>
              Complete transparency into how your AI Actor is trained and what knowledge it uses
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 text-sm" data-testid="guide-body">
            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">How this page works</h3>
              <p className="text-gray-700">This is your <b>AI Self-Learning</b> console. You enable training for an actor, your trainers rate the AI's replies on the actor's <b>public profile link</b>, you review those ratings here, and approved fixes feed back into the Knowledge Base or Coaching Notes. Metrics then show real progress.</p>
              <p className="text-gray-700 mt-2">Flow: <b>Setup</b> → trainers rate on the public link → <b>Review Queue</b> → Approve to KB / Coaching → <b>Insights</b>.</p>
            </section>

            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">🎯 Dashboard</h3>
              <div className="space-y-1 text-gray-700">
                <p>A quick overview of the selected actor: the live <b>Quality %</b> and <b>Training Progress %</b>, the linked Knowledge Bases, the number of coaching notes, recent feedback counts, and the guard-rail configuration. (A legacy readiness score is also shown for reference.)</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">🎓 Setup</h3>
              <div className="space-y-1 text-gray-700">
                <p><b>Training Mode</b> — turn training on for this actor. When ON, its public link asks visitors to identify themselves.</p>
                <p><b>Trainers</b> — add people by <b>name + email only (no password)</b>. Only these emails get rating controls on the public link.</p>
                <p><b>Targets &amp; weights</b> — your goals for examples reviewed, KB chunk coverage %, and signals resolved (used to compute Training Progress %).</p>
                <p><b>Corrected-chunk boost</b> — how strongly approved corrections outrank regular chunks during search.</p>
                <p><b>Live-signal detection</b> (opt-in) — auto-flag dissatisfied real users into the queue (uses 1 extra LLM call per turn). <b>Relevance-scoped coaching</b> (opt-in) — inject only the coaching notes relevant to each question.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">📥 Review Queue</h3>
              <div className="space-y-1 text-gray-700">
                <p>All pending ratings awaiting your decision. Each card shows whether it's a <b>🟢 Trainer rating</b> or a <b>🟣 Live signal</b>, a <b>Fact</b> or <b>Behavioral</b> tag, the question, the AI's answer, the reason, and the audit (which document/chunk was used).</p>
                <p><b>Approve → Knowledge Base</b> — for a fact: writes the correction into the relevant document chunk and gives it a retrieval boost.</p>
                <p><b>Approve → Coaching</b> — for behavior/tone: adds a coaching note injected into the AI's instructions.</p>
                <p><b>Dismiss</b> — a false alarm; nothing changes.</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">📊 Insights</h3>
              <div className="space-y-1 text-gray-700">
                <p><b>Quality %</b> — approval rate + grounded rate. <b>Training Progress %</b> — weighted blend of examples reviewed, chunk coverage, and signals resolved.</p>
                <p><b>KB Chunk Coverage</b> — measured by the share of <b>chunks</b> exercised in training (not whole documents), with a per-document coverage table.</p>
                <p><b>Live signals pending</b> and a <b>Quality % over time</b> trend (builds up over days).</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-indigo-600 mb-2">⚡ How a correction improves answers</h3>
              <p className="text-gray-700">When you approve a fact, the corrected chunk gets an extra retrieval weight, so it ranks above stale or competing chunks for similar questions. Over time this lowers repeat mistakes and raises the grounded rate.</p>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
